import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Layers, Loader2, FolderOpen, Trash2, AlertTriangle, GitMerge, ArrowLeft, ArrowRight, Pencil } from 'lucide-react';
import { useReadOnly } from '../../context/ReadOnlyContext';
import useFusionPtrStore from '../../stores/useFusionPtrStore';
import { fusionPtrService } from '../../services/fusionPtrService';
import FusionAnalysisSelector from './FusionAnalysisSelector';
import { formatUpdatedAgo } from './fusionFormat';
import AnalysisDetailModal from './AnalysisDetailModal';
import CreateFusionModal from './CreateFusionModal';
import DeleteConfirmationModal from '../common/DeleteConfirmationModal';
import HolisticCompare from './HolisticCompare';
import FusionWorkspace from './FusionWorkspace';

// Standalone page: Fusionnement des PTR.
const FusionPTRPage = () => {
  const { t } = useTranslation();
  const { isViewer } = useReadOnly();
  const [fusionName, setFusionName] = useState('');
  const [saving, setSaving] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const panelRef = useRef(null);
  const selectorRef = useRef(null);

  const analysesLoading = useFusionPtrStore((s) => s.analysesLoading);
  const analyses = useFusionPtrStore((s) => s.analyses);
  const loadAnalyses = useFusionPtrStore((s) => s.loadAnalyses);
  const selectedIds = useFusionPtrStore((s) => s.selectedIds);
  const snapshots = useFusionPtrStore((s) => s.snapshots);
  const collect = useFusionPtrStore((s) => s.collect);
  const collectLoading = useFusionPtrStore((s) => s.collectLoading);
  const fusions = useFusionPtrStore((s) => s.fusions);
  const loadFusions = useFusionPtrStore((s) => s.loadFusions);
  const currentFusion = useFusionPtrStore((s) => s.currentFusion);
  const openFusion = useFusionPtrStore((s) => s.openFusion);
  const closeFusion = useFusionPtrStore((s) => s.closeFusion);
  const error = useFusionPtrStore((s) => s.error);
  const clearError = useFusionPtrStore((s) => s.clearError);

  useEffect(() => {
    loadAnalyses();
    loadFusions();
    // Session snapshot cache would otherwise serve pre-edit cards/modals —
    // every page visit starts fresh (refetch is a single POST, on demand).
    useFusionPtrStore.setState({ snapshotCache: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToPanel = () => {
    requestAnimationFrame(() => {
      panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const handleToggleFusion = (id) => {
    if (String(currentFusion?._id) === String(id)) {
      closeFusion();
    } else {
      openFusion(id).then(scrollToPanel);
    }
  };

  const handleInitiateFusion = () => {
    if (!selectedIds.length) return;
    const selectedNames = (analyses || [])
      .filter((a) => selectedIds.includes(String(a._id || a.id)))
      .map((a) => a.name);
    const defaultName = selectedNames.length
      ? `Fusion PTR — ${selectedNames.join(' & ')}`
      : `Fusion PTR (${selectedIds.length} analyses)`;
    setFusionName(defaultName);
    setShowCreateModal(true);
  };

  const handleSaveModal = async (e) => {
    if (e) e.preventDefault();
    if (!fusionName.trim() || !selectedIds.length) return;
    setSaving(true);
    try {
      const response = await fusionPtrService.createFusion(fusionName.trim(), selectedIds);
      setFusionName('');
      setShowCreateModal(false);
      await loadFusions();
      if (response.data?._id) {
        await openFusion(response.data._id);
        scrollToPanel();
      }
    } catch (err) {
      useFusionPtrStore.setState({ error: err.message });
    } finally {
      setSaving(false);
    }
  };

  // Source analysis names for the saved-fusion chips (deleted sources resolve to null).
  const analysisNameById = useMemo(() => new Map(
    (analyses || []).map((a) => [String(a._id || a.id), a.name]),
  ), [analyses]);

  const handleDelete = async (id) => {
    try {
      await fusionPtrService.deleteFusion(id);
      loadFusions();
    } catch (e) {
      useFusionPtrStore.setState({ error: e.message });
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleRefresh = async () => {
    if (!currentFusion) return;
    try {
      await fusionPtrService.refreshFusion(currentFusion._id);
      await openFusion(currentFusion._id);
      // Drop the refreshed analyses from the session snapshot cache so the
      // holistic view and detail modals refetch instead of serving pre-sync rows.
      const ids = (currentFusion.sourceAnalysisIds || []).map(String);
      if (ids.length) {
        const cache = { ...useFusionPtrStore.getState().snapshotCache };
        ids.forEach((id) => { delete cache[id]; });
        useFusionPtrStore.setState({ snapshotCache: cache });
      }
    } catch (e) {
      useFusionPtrStore.setState({ error: e.message });
      throw e;
    }
  };

  const handleCloseCreateModal = useCallback(() => setShowCreateModal(false), []);

  const saveNameEdit = async () => {
    const trimmed = nameDraft.trim();
    setEditingName(false);
    if (!trimmed || !currentFusion || trimmed === currentFusion.name) return;
    try {
      await fusionPtrService.updateFusion(currentFusion._id, { name: trimmed });
      await openFusion(currentFusion._id);
    } catch (e) {
      useFusionPtrStore.setState({ error: e.message });
    }
  };

  if (currentFusion) {
    return (
      <div className="max-w-[1600px] mx-auto space-y-4">
        {/* Breadcrumb navigation bar */}
        <div className="flex items-center justify-between bg-white rounded-xl shadow-xs border border-slate-200 px-5 py-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={closeFusion}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
            >
              <ArrowLeft size={14} />
              {t('fusionPtr.backToFusions', 'Retour aux fusions')}
            </button>
            <div className="h-4 w-px bg-slate-200" />
            <div className="flex items-center gap-2 text-xs min-w-0">
              <span className="text-slate-400 flex-shrink-0">{t('fusionPtr.savedFusions', 'Fusions enregistrées')}</span>
              <span className="text-slate-300 flex-shrink-0">/</span>
              {editingName ? (
                <input
                  autoFocus
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onBlur={saveNameEdit}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveNameEdit();
                    if (e.key === 'Escape') setEditingName(false);
                  }}
                  className="font-bold text-slate-800 text-xs border border-blue-400 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-400 min-w-0 flex-1"
                />
              ) : (
                <>
                  <span className="font-bold text-slate-800 truncate" title={currentFusion.name}>{currentFusion.name}</span>
                  {!isViewer && (
                    <button
                      type="button"
                      onClick={() => { setNameDraft(currentFusion.name || ''); setEditingName(true); }}
                      title={t('fusionPtr.renameFusion', 'Renommer la fusion')}
                      className="p-1 rounded text-slate-300 hover:text-blue-600 hover:bg-blue-50 transition-colors flex-shrink-0"
                    >
                      <Pencil size={12} />
                    </button>
                  )}
                </>
              )}
              <span className="text-slate-400 tabular-nums flex-shrink-0">
                {t('fusionPtr.workspaceSubtitle', '{{count}} analyse(s) source(s)', { count: (currentFusion.sourceAnalysisIds || []).length })}
              </span>
            </div>
          </div>
          <span className={`text-[10px] px-2.5 py-1 rounded-full font-bold uppercase tracking-wider ${currentFusion.status === 'validated' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
            {currentFusion.status === 'validated' ? t('fusionPtr.statusValidated', 'Validée') : t('fusionPtr.statusDraft', 'Brouillon')}
          </span>
        </div>

        {/* Dedicated Workspace */}
        <FusionWorkspace
          key={currentFusion._id}
          fusion={currentFusion}
          onChanged={() => openFusion(currentFusion._id)}
          onRefresh={handleRefresh}
        />
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] mx-auto space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center text-sm text-slate-500 mb-2">
                <span className="text-blue-600 font-medium">
                  {t('fusionPtr.navigationTitle', 'Fusionnement des PTR')}
                </span>
              </div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center">
                <Layers size={24} className="mr-3 text-blue-600" />
                {t('fusionPtr.navigationTitle', 'Fusionnement des PTR')}
              </h1>
              <p className="text-slate-600 text-sm mt-1">
                {t('fusionPtr.pageDesc', 'Vue globale et PTR optimisé multi-analyses')}
              </p>
            </div>
          </div>
        </div>
        <div className="px-6 py-3 bg-slate-50 border-b border-slate-100">
          <dl className="flex flex-wrap items-center divide-x divide-slate-200 text-sm">
            <div className="flex items-center gap-2 pr-4 text-slate-600">
              <span className="w-2 h-2 rounded-full bg-blue-500" aria-hidden="true" />
              <span>
                {t('fusionPtr.statusSelection', '{{count}} analyse(s) sélectionnée(s)', { count: selectedIds.length })}
              </span>
            </div>
            {snapshots.length > 0 && (
              <div className="px-4 text-slate-500 tabular-nums">
                {t('fusionPtr.statusSnapshots', '{{count}} analyse(s) collectée(s)', { count: snapshots.length })}
              </div>
            )}
            <div className="pl-4 text-slate-500 tabular-nums">
              {t('fusionPtr.fusionsCount', '{{count}} fusion(s)', { count: fusions?.length || 0 })}
            </div>
          </dl>
        </div>
      </div>
      <div className="space-y-3">
        {isViewer && (
          <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <AlertTriangle size={14} />
            {t('fusionPtr.readOnly', 'Lecture seule : la création et la validation de fusions sont désactivées pour ce rôle.')}
          </div>
        )}
        {error && (
          <div className="flex items-center gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            <AlertTriangle size={14} />
            <span className="flex-1">{error}</span>
            <button type="button" onClick={clearError} className="font-semibold hover:underline">{t('common.close', 'Fermer')}</button>
          </div>
        )}
      </div>

      {analysesLoading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 bg-white rounded-xl border border-slate-200 p-6">
          <Loader2 size={16} className="animate-spin" />
          {t('common.loading', 'Chargement...')}
        </div>
      ) : (
        <div ref={selectorRef} className="scroll-mt-4">
          <FusionAnalysisSelector
            disabled={saving}
            onStartFusion={handleInitiateFusion}
            canCollect={!isViewer}
            onOpenDetails={setDetailId}
          />
        </div>
      )}
      {detailId && (
        <AnalysisDetailModal
          analysisId={detailId}
          canSelect={!isViewer}
          onClose={() => setDetailId(null)}
        />
      )}

      <CreateFusionModal
        isOpen={showCreateModal}
        onClose={handleCloseCreateModal}
        onSubmit={handleSaveModal}
        fusionName={fusionName}
        setFusionName={setFusionName}
        selectedAnalyses={(analyses || []).filter((a) => selectedIds.includes(String(a._id || a.id)))}
        saving={saving}
      />

      <DeleteConfirmationModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && handleDelete(deleteTarget._id)}
        title={t('common.delete', 'Supprimer')}
        message={deleteTarget ? t('fusionPtr.deleteFusionConfirm', 'Supprimer définitivement la fusion « {{name}} » ?', { name: deleteTarget.name }) : ''}
      />

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-5 py-3.5 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FolderOpen size={16} className="text-slate-500" />
            <h3 className="text-sm font-bold text-slate-800">{t('fusionPtr.savedFusions', 'Fusions enregistrées')}</h3>
          </div>
          {(fusions?.length > 0) && (
            <span className="text-xs font-semibold text-slate-600 bg-white border border-slate-200 rounded-full px-2.5 py-0.5 tabular-nums">
              {t('fusionPtr.fusionsCount', '{{count}} fusion(s)', { count: fusions.length })}
            </span>
          )}
        </div>

        {!(fusions?.length) ? (
          <div className="p-10 text-center">
            <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-slate-400">
              <FolderOpen size={20} />
            </div>
            <p className="text-sm text-slate-500">
              {t('fusionPtr.noFusions', 'Aucune fusion enregistrée pour le moment.')}
            </p>
          </div>
        ) : (
          <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            {fusions.map((f) => {
              const validated = f.status === 'validated';
              const proposals = f.proposals || [];
              const decided = proposals.filter((p) => p.status !== 'pending').length;
              const sourceIds = (f.sourceAnalysisIds || []).map(String);
              const sourceNames = sourceIds
                .map((id) => analysisNameById.get(id))
                .filter(Boolean);
              const shownSources = sourceNames.slice(0, 3);
              const hiddenSources = sourceIds.length - shownSources.length;

              return (
                <div
                  key={f._id}
                  className="bg-white rounded-xl border border-slate-200 shadow-2xs hover:shadow-md hover:border-blue-300 transition-all duration-200 p-5 flex flex-col justify-between gap-4"
                >
                  <div>
                    <div className="flex items-start justify-between gap-3 mb-2.5">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                        validated ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${validated ? 'bg-green-500' : 'bg-blue-500'}`} />
                        {validated ? t('fusionPtr.statusValidated', 'Validée') : t('fusionPtr.statusDraft', 'Brouillon')}
                      </span>
                      {!isViewer && (
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(f)}
                          className="p-1 rounded-md text-slate-300 hover:text-red-600 hover:bg-red-50 transition-colors"
                          title={t('common.delete', 'Supprimer')}
                        >
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={() => openFusion(f._id)}
                      className="text-left group block w-full"
                    >
                      <h4 className="text-sm font-bold text-slate-800 line-clamp-2 group-hover:text-blue-600 transition-colors">
                        {f.name}
                      </h4>
                    </button>

                    <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                      {shownSources.map((name) => (
                        <span key={name} className="inline-flex items-center px-2 py-0.5 rounded bg-slate-100 text-slate-600 text-xs font-medium max-w-[180px]">
                          <span className="truncate">{name}</span>
                        </span>
                      ))}
                      {hiddenSources > 0 && (
                        <span className="text-[11px] font-medium text-slate-500 bg-slate-50 border border-slate-200 px-1.5 py-0.5 rounded">
                          {t('fusionPtr.moreSources', '+{{count}}', { count: hiddenSources })}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mt-auto pt-3 border-t border-slate-100 space-y-3">
                    {proposals.length > 0 && (
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between text-xs text-slate-500">
                          <span>{t('fusionPtr.decided', '{{done}} / {{total}} décidées', { done: decided, total: proposals.length })}</span>
                          <span className="font-semibold tabular-nums">{Math.round((decided / proposals.length) * 100)}%</span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-300 ${validated ? 'bg-green-500' : 'bg-blue-500'}`}
                            role="progressbar"
                            aria-valuenow={decided}
                            aria-valuemin={0}
                            aria-valuemax={proposals.length}
                            style={{ width: `${(decided / proposals.length) * 100}%` }}
                          />
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-between text-xs text-slate-400">
                      <div className="flex items-center gap-1.5 truncate text-[11px]">
                        {f.createdByName && <span>{t('fusionPtr.createdBy', 'Par {{name}}', { name: f.createdByName })}</span>}
                        {f.createdByName && f.updatedAt && <span>·</span>}
                        {f.updatedAt && <span>{formatUpdatedAgo(t, f.updatedAt)}</span>}
                      </div>
                      <button
                        type="button"
                        onClick={() => openFusion(f._id)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 hover:bg-blue-600 text-blue-700 hover:text-white font-semibold text-xs rounded-lg transition-colors flex-shrink-0"
                      >
                        {t('fusionPtr.openWorkspace', "Ouvrir")}
                        <ArrowRight size={13} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default FusionPTRPage;
